import { useEffect, useState } from 'react';
import {
  TrendingUp,
  Users,
  Database,
  AlertTriangle,
  DollarSign,
  Activity,
  CheckCircle,
} from 'lucide-react';

interface DashboardStats {
  batches: {
    total: number;
    processing: number;
    completed: number;
    today: number;
  };
  contacts: {
    totalProcessed: number;
    todayProcessed: number;
  };
  revenue: {
    total: number;
    today: number;
    profit: number;
  };
  cache: {
    hitRate: number;
    globalSize: number;
  };
  issues: {
    unresolved: number;
    critical: number;
  };
}

interface RecoveryPendingLocation {
  locationId: string;
  billingDisplayName: string | null;
  accountSid: string;
  unresolvedCount: number;
  oldestPendingAt: string;
}

interface TagWritesPendingSummary {
  queued: number;
  processing: number;
  failed: number; // failures within failedWindowHours only (recent), not all-time
  failedWindowHours?: number;
  pendingLocationCount: number;
  oldestPendingAt: string | null;
}

interface RecoveryPendingSummary {
  locationCount: number;
  totalUnresolvedEvents: number;
  oldestPendingAt: string | null;
  locations: RecoveryPendingLocation[];
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [recentBatches, setRecentBatches] = useState<any[]>([]);
  const [recentIssues, setRecentIssues] = useState<any[]>([]);
  const [recoveryPending, setRecoveryPending] = useState<RecoveryPendingSummary | null>(null);
  const [tagWritesPending, setTagWritesPending] = useState<TagWritesPendingSummary | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch multiple endpoints in parallel
      const [batchesRes, cacheRes, issuesRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL}/admin/batches?limit=5`, {
          credentials: 'include',
        }),
        fetch(`${import.meta.env.VITE_API_URL}/admin/cache/stats`, {
          credentials: 'include',
        }),
        fetch(`${import.meta.env.VITE_API_URL}/admin/issues/stats`, {
          credentials: 'include',
        }),
      ]);

      const [batchesData, cacheData, issuesData] = await Promise.all([
        batchesRes.json() as Promise<any>,
        cacheRes.json() as Promise<any>,
        issuesRes.json() as Promise<any>,
      ]);

      // Calculate stats from responses
      const today = new Date().toISOString().split('T')[0];
      const batches = batchesData.batches || [];
      const todayBatches = batches.filter(
        (b: any) => b.created_at?.startsWith(today)
      );
      const processingBatches = batches.filter(
        (b: any) =>
          b.status === 'pending' ||
          b.status === 'upload_complete' ||
          b.status === 'processing'
      );

      // Calculate revenue from batches
      let totalRevenue = 0;
      let todayRevenue = 0;
      let totalProfit = 0;
      let totalProcessed = 0;
      let todayProcessed = 0;

      batches.forEach((batch: any) => {
        totalRevenue += parseFloat(batch.actual_revenue) || 0;
        totalProfit +=
          (parseFloat(batch.actual_revenue) || 0) -
          (parseFloat(batch.actual_cost) || 0);
        totalProcessed += batch.processed_count || 0;

        if (batch.created_at?.startsWith(today)) {
          todayRevenue += parseFloat(batch.actual_revenue) || 0;
          todayProcessed += batch.processed_count || 0;
        }
      });

      setStats({
        batches: {
          total: batchesData.totalCount || 0,
          processing: processingBatches.length,
          completed: batches.filter((b: any) => b.status === 'completed').length,
          today: todayBatches.length,
        },
        contacts: {
          totalProcessed,
          todayProcessed,
        },
        revenue: {
          total: totalRevenue,
          today: todayRevenue,
          profit: totalProfit,
        },
        cache: {
          hitRate: cacheData.success ? cacheData.performance?.cacheHitRate || 0 : 0,
          globalSize: cacheData.success
            ? cacheData.globalCache?.totalEntries || 0
            : 0,
        },
        issues: {
          unresolved: issuesData.success ? issuesData.stats?.totalUnresolved || 0 : 0,
          critical: issuesData.success ? issuesData.stats?.bySeverity?.critical || 0 : 0,
        },
      });

      setRecentBatches(batches.slice(0, 5));

      // Fetch recent issues
      const recentIssuesRes = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/issues?limit=5`,
        { credentials: 'include' }
      );
      const recentIssuesData = await recentIssuesRes.json() as any;
      setRecentIssues(recentIssuesData.issues || []);

      // Stage 1d: Twilio webhook event recovery summary across all locations
      try {
        const recoveryRes = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/billing/recovery-pending-summary`,
          { credentials: 'include' }
        );
        const recoveryData = (await recoveryRes.json()) as any;
        if (recoveryData.success) {
          setRecoveryPending(recoveryData.summary || null);
        }
      } catch (err) {
        console.error('[AdminDashboard] Recovery summary error:', err);
      }

      // Phase 4: GHL tag-writes pending across all locations
      try {
        const tagWritesRes = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/billing/tag-writes-pending-summary`,
          { credentials: 'include' }
        );
        const tagWritesData = (await tagWritesRes.json()) as any;
        if (tagWritesData.success) {
          setTagWritesPending(tagWritesData.summary || null);
        }
      } catch (err) {
        console.error('[AdminDashboard] Tag writes summary error:', err);
      }
    } catch (error) {
      console.error('[AdminDashboard] Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stage 1d: Twilio webhook event recovery banner. Renders only when at least
          one location has unresolved rejected events. Self-cleans once admin
          replays them (or the underlying SID config is fixed and no new ones land). */}
      {recoveryPending && recoveryPending.locationCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">
              Twilio webhook events pending recovery
            </p>
            <p className="text-sm text-amber-800 mt-1">
              <strong>{recoveryPending.totalUnresolvedEvents.toLocaleString()}</strong> rejected
              events across <strong>{recoveryPending.locationCount}</strong>{' '}
              {recoveryPending.locationCount === 1 ? 'location' : 'locations'}
              {recoveryPending.oldestPendingAt && (
                <>
                  {' '}
                  · oldest pending {formatTimeAgo(recoveryPending.oldestPendingAt)}
                </>
              )}
              . Open Location Billing for each location to replay.
            </p>
            {recoveryPending.locations.length > 0 && (
              <ul className="mt-2 text-xs text-amber-800 space-y-0.5">
                {recoveryPending.locations.slice(0, 5).map(loc => (
                  <li key={loc.locationId}>
                    <button
                      onClick={() => {
                        window.history.pushState({}, '', `/admin/billing/locations/${loc.locationId}`);
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                      className="text-amber-900 underline hover:text-amber-700"
                    >
                      {loc.billingDisplayName || loc.locationId}
                    </button>
                    : {loc.unresolvedCount.toLocaleString()} pending
                  </li>
                ))}
                {recoveryPending.locations.length > 5 && (
                  <li className="text-amber-700 italic">
                    +{recoveryPending.locations.length - 5} more
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Phase 4: GHL tag-writes pending banner. Shows only when there's a live
          backlog (queued/processing) or RECENT failures (last failedWindowHours).
          Terminal failures are intentionally windowed so a one-off bad import
          doesn't keep the banner lit forever — this is an "is it broken right
          now?" signal, not an all-time error tally. */}
      {tagWritesPending && (tagWritesPending.queued + tagWritesPending.processing + tagWritesPending.failed) > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
          <Activity className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-900">
              GHL tag writes
            </p>
            <p className="text-sm text-blue-800 mt-1">
              {tagWritesPending.queued > 0 && (
                <><strong>{tagWritesPending.queued.toLocaleString()}</strong> queued</>
              )}
              {tagWritesPending.queued > 0 && tagWritesPending.processing > 0 && ', '}
              {tagWritesPending.processing > 0 && (
                <><strong>{tagWritesPending.processing.toLocaleString()}</strong> processing</>
              )}
              {tagWritesPending.pendingLocationCount > 0 && (
                <>
                  {' '}across <strong>{tagWritesPending.pendingLocationCount}</strong>{' '}
                  {tagWritesPending.pendingLocationCount === 1 ? 'location' : 'locations'}
                </>
              )}
              {tagWritesPending.oldestPendingAt && (
                <> · oldest pending {formatTimeAgo(tagWritesPending.oldestPendingAt)}</>
              )}
              {tagWritesPending.failed > 0 && (
                <>
                  {' '}·{' '}
                  <strong className="text-red-700">
                    {tagWritesPending.failed.toLocaleString()} failed
                    {tagWritesPending.failedWindowHours
                      ? ` (last ${tagWritesPending.failedWindowHours}h)`
                      : ''}
                  </strong>
                </>
              )}
              .
            </p>
            <p className="text-xs text-blue-700 mt-1">
              GHLTagWriteWorkflow drains the queue every minute. Persistent
              backlog suggests GHL rate-limit pressure on specific locations
              or expired OAuth tokens. Failure count is recent only
              {tagWritesPending.failedWindowHours
                ? ` (last ${tagWritesPending.failedWindowHours}h)`
                : ''}
              ; see the Issues log for the full history.
            </p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={`$${(stats?.revenue.total || 0).toFixed(2)}`}
          subtitle={`$${(stats?.revenue.today || 0).toFixed(2)} today`}
          icon={DollarSign}
          iconBg="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Total Batches"
          value={stats?.batches.total || 0}
          subtitle={`${stats?.batches.processing || 0} processing`}
          icon={Activity}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Cache Hit Rate"
          value={`${(stats?.cache.hitRate || 0).toFixed(1)}%`}
          subtitle={`${stats?.cache.globalSize || 0} cached numbers`}
          icon={Database}
          iconBg="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          title="Open Issues"
          value={stats?.issues.unresolved || 0}
          subtitle={
            stats?.issues.critical
              ? `${stats.issues.critical} critical`
              : 'No critical issues'
          }
          icon={AlertTriangle}
          iconBg={stats?.issues.critical ? 'bg-red-100' : 'bg-yellow-100'}
          iconColor={stats?.issues.critical ? 'text-red-600' : 'text-yellow-600'}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="Total Profit"
          value={`$${(stats?.revenue.profit || 0).toFixed(2)}`}
          icon={TrendingUp}
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
          small
        />
        <StatCard
          title="Contacts Processed"
          value={stats?.contacts.totalProcessed || 0}
          subtitle={`${stats?.contacts.todayProcessed || 0} today`}
          icon={Users}
          iconBg="bg-indigo-100"
          iconColor="text-indigo-600"
          small
        />
        <StatCard
          title="Completed Batches"
          value={stats?.batches.completed || 0}
          subtitle={`${stats?.batches.today || 0} today`}
          icon={CheckCircle}
          iconBg="bg-teal-100"
          iconColor="text-teal-600"
          small
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Batches */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Batches</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {recentBatches.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                No batches found
              </div>
            ) : (
              recentBatches.map((batch: any) => (
                <div key={batch.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {batch.batch_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {batch.location_name || batch.ghl_location_id}
                      </p>
                    </div>
                    <div className="ml-4 flex items-center space-x-3">
                      <StatusBadge status={batch.status} />
                      <span className="text-xs text-gray-500">
                        {formatTimeAgo(batch.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Issues */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Issues</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {recentIssues.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                No recent issues
              </div>
            ) : (
              recentIssues.map((issue: any) => (
                <div key={issue.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2">
                        <SeverityBadge severity={issue.severity} />
                        <span className="text-xs text-gray-500">
                          {issue.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1 line-clamp-1">
                        {issue.message || issue.title}
                      </p>
                    </div>
                    <span className="ml-4 text-xs text-gray-500 whitespace-nowrap">
                      {formatTimeAgo(issue.created_at)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: typeof TrendingUp;
  iconBg: string;
  iconColor: string;
  small?: boolean;
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconBg,
  iconColor,
  small,
}: StatCardProps) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${small ? 'p-4' : 'p-6'}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`${small ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 mt-1`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`${iconBg} p-3 rounded-xl`}>
          <Icon className={`${small ? 'w-5 h-5' : 'w-6 h-6'} ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }: { status: string }) {
  const getStatusStyles = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'upload_complete':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusStyles()}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}

// Severity Badge Component
function SeverityBadge({ severity }: { severity: string }) {
  const getSeverityStyles = () => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'error':
        return 'bg-orange-100 text-orange-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getSeverityStyles()}`}>
      {severity}
    </span>
  );
}

// Time formatting helper
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}
